import * as React from 'react';
import { SWRConfig } from 'swr';
import { useMockData } from '../contexts/MockDataContext';

type _Props = {
  children: React.ReactNode;
  debug?: boolean;
};

export const SWRInterceptor: React.FC<_Props> = ({ children, debug = false }) => {
  const { activeMocks, findMockForEndpoint, mockConfig, mocks } = useMockData();

  // Log active mocks on mount and when they change
  React.useEffect(() => {
    if (!debug) return;

    console.info('[SWR Mock] Active mocks:', {
      activeMocks,
      mockDetails: activeMocks.map((id) => mocks[id]),
      mockConfig,
    });
  }, [activeMocks, debug, mocks, mockConfig]);

  // Simplified fetcher - no auth header handling since you won't make authenticated calls
  const interceptor = async (resource: string | [string, string], init?: RequestInit): Promise<unknown> => {
    const url = Array.isArray(resource) ? resource[0] : resource;
    const token = Array.isArray(resource) ? resource[1] : null;

    debug && console.info('[SWR Mock] Request:', { url, token, mockConfig, enabled: mockConfig.enabled });

    // Try to find a mock for this endpoint
    const mockItem = findMockForEndpoint(url);
    debug &&
      console.info('[SWR Mock] Found mock?', {
        url,
        mockFound: !!mockItem,
        mockId: mockItem?.id,
        mockEndpoint: mockItem?.endpoint,
      });

    // Check if we should force loading state
    if (mockConfig.enabled && mockConfig.forceLoading) {
      return new Promise<unknown>((resolve) => {
        setTimeout(() => {
          if (mockItem) {
            debug &&
              console.info(`[SWR Mock] Resolved after forced loading for ${url}`, {
                mockId: mockItem.id,
                mockData: mockItem.data,
                loadingDuration: mockConfig.loadingDuration,
              });
            resolve(mockItem.data);
          } else {
            debug && console.info(`[SWR Mock] No mock found after loading, using real fetch for ${url}`);
            performSimpleFetch(url, token, init).then(resolve);
          }
        }, mockConfig.loadingDuration);
      });
    }

    // Check if mocking is enabled
    if (!mockConfig.enabled) {
      debug && console.info(`[SWR Mock] Mocking disabled, using real fetch for ${url}`);
      return performSimpleFetch(url, token, init);
    }

    // If no mock found, proceed with real fetch
    if (!mockItem) {
      debug && console.info(`[SWR Mock] No mock found for ${url}, using real fetch`);
      return performSimpleFetch(url, token, init);
    }

    // Simulate network delay
    if (mockConfig.delay > 0) {
      debug && console.info(`[SWR Mock] Delaying response for ${url} by ${mockConfig.delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, mockConfig.delay));
    }

    // Simulate random failures if configured
    if (mockConfig.failureRate > 0 && Math.random() < mockConfig.failureRate) {
      const statusCode = mockConfig.statusCode || 500;
      const errorMessage = mockConfig.errorMessage || 'Mock server error';

      debug &&
        console.info(`[SWR Mock] Simulating failure for ${url}`, {
          statusCode,
          errorMessage,
          failureRate: mockConfig.failureRate,
        });

      const error = new Error(errorMessage) as Error & { status?: number };
      (error as Error & { status?: number }).status = statusCode;
      throw error;
    }

    // Log the intercepted request
    debug &&
      console.info(`[SWR Mock] Intercepted request to ${url}`, {
        mockId: mockItem.id,
        mockData: mockItem.data,
      });

    // Return the mock data
    return mockItem.data;
  };

  const performSimpleFetch = async (url: string, token: string | null, init?: RequestInit): Promise<unknown> => {
    debug && console.info(`[SWR Mock] Performing simple fetch for ${url}`);

    try {
      const response = await fetch(url, {
        ...init,
        headers: new Headers({
          ...init?.headers,
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));

        console.error(`[SWR Mock] Fetch error for ${url}`, {
          status: response.status,
          error,
        });

        const customError = new Error(error.message || 'An error occurred') as Error & {
          info?: unknown;
          status?: number;
        };
        (customError as Error & { status?: number }).status = response.status;
        (customError as Error & { info?: unknown }).info = error.errors || error;
        throw customError;
      }

      const data = await response.json();
      debug && console.info(`[SWR Mock] Fetch success for ${url}`, { dataPreview: data });
      return data;
    } catch (err) {
      console.error(`[SWR Mock] Fetch exception for ${url}`, err);
      throw err;
    }
  };

  return (
    <SWRConfig
      value={{
        provider: () => new Map(),
        revalidateIfStale: true,
        keepPreviousData: true,
        fetcher: interceptor,
        errorRetryCount: 3,
        onErrorRetry: (error, key, config, revalidate, opts) => {
          const maxRetryCount = config.errorRetryCount;
          const retryCount = opts.retryCount;
          const errorsToRetry = [429, 502, 503];

          // Don't retry on client errors (4xx) since no auth handling needed
          // if (error.status >= 400 && error.status < 500) return;
          if (!errorsToRetry.includes(error.status)) return;

          // Exponential backoff
          const timeout =
            ~~((Math.random() + 0.5) * (1 << (retryCount < 8 ? retryCount : 8))) * (config.errorRetryInterval || 1000);

          if (maxRetryCount !== undefined && retryCount > maxRetryCount) {
            return;
          }

          if (process.env.NODE_ENV === 'development') {
            console.debug(`[SWR Mock] Retry attempt #${retryCount}, ${key}`);
          }
          setTimeout(revalidate, timeout, opts);
        },
      }}
    >
      {children}
    </SWRConfig>
  );
};
